<template>
  <card-base
    :loading="loading"
    :disabled="internalDisabled"
    :show-actions="showActions"
    :show-actions-cancel="showActions && internalMode !== 'NEW'"
    :button-submit-text="buttonSubmitText"
    v-bind="$attrs"
    @cancel="onCancel"
    v-on="$listeners"
  >
    <!-- status -->
    <template v-if="$slots.status && !loading" #titleAvatar>
      <slot name="status" />
    </template>

    <!-- actions -->
    <template v-if="showToolbarActions" #toolbarIconActions>
      <slot name="toolbar-icon-actions-prepend" />
      <notes-dialog
        v-if="showActionNotes"
        :noteable-id="parseInt(modelId)"
        :noteable-type="modelType"
        :enable-notifications="enableNotesNotifications"
      />
      <button-tooltip
        v-if="showActionEdit"
        icon="$freshvuecards-edit"
        @click="onEdit"
      />
      <slot name="toolbar-icon-actions-append" />
    </template>

    <!-- expand prepend -->
    <template v-if="!loading" #expandPrepend>
      <slot name="expand-prepend" />
    </template>

    <!-- data -->
    <!-- VIEW -->
    <template v-if="internalMode === 'VIEW'">
      <!-- loading -->
      <v-container v-if="loading">
        <v-row align="center" justify="center">
          <v-progress-circular indeterminate color="primary" class="ma-12" />
        </v-row>
      </v-container>
      <!-- no data -->
      <v-container v-else-if="!modelId">
        <v-row align="center" justify="center">
          {{ $t('fresh-vue-cards.CardModelDetails.no-data') }}
        </v-row>
      </v-container>
      <!-- data -->
      <template v-else>
        <!-- details & actions -->
        <section>
          <v-row>
            <!-- details -->
            <v-col
              cols="12"
              :md="$slots['view-actions'] ? 6 : 12"
              :lg="$slots['view-actions'] ? 8 : 12"
              :xl="$slots['view-actions'] ? 9 : 12"
            >
              <slot name="view-details" />
            </v-col>
            <!-- actions -->
            <v-col v-if="$slots['view-actions']" cols="12" md="6" lg="4" xl="3">
              <v-card outlined class="fill-height">
                <slot name="view-actions" />
              </v-card>
            </v-col>
          </v-row>
        </section>

        <!-- expansion -->
        <section v-if="$slots['view-expansion']">
          <v-row>
            <v-col cols="12">
              <slot name="view-expansion" />
            </v-col>
          </v-row>
        </section>

        <!-- relations -->
        <card-base
          v-if="tabs && $scopedSlots['view-relations']"
          class="mt-5"
          :flat="$vuetify.breakpoint.mobile"
          :no-padding="$vuetify.breakpoint.mobile"
        >
          <template #expandPrepend>
            <tabs-base v-model="internalTab" :tabs="tabs" />
          </template>

          <slot
            name="view-relations"
            :tab="internalTab"
            :set-tab="(t) => (internalTab = t)"
          />
        </card-base>
      </template>
    </template>
    <!-- EDIT -->
    <template v-else-if="internalMode === 'EDIT'">
      <slot name="edit" />
    </template>
    <!-- NEW -->
    <template v-else-if="internalMode === 'NEW'">
      <slot name="create" />
    </template>
    <!-- ERROR -->
    <template v-else>
      <v-alert type="error">{{ $t('fresh-vue-cards.misc.error') }}</v-alert>
    </template>
  </card-base>
</template>

<script>
import {
  VContainer,
  VRow,
  VCol,
  VProgressCircular,
  VCard,
  VAlert
} from 'vuetify/lib'

import CardBase from './CardBase'
import { TabsBase } from '@freshsolutions/fresh-vue'
import { ButtonTooltip } from '@freshsolutions/fresh-vue'

export default {
  name: 'CardModelDetails',

  components: {
    VContainer,
    VRow,
    VCol,
    VProgressCircular,
    VCard,
    VAlert,
    CardBase,
    TabsBase,
    ButtonTooltip
  },

  inheritAttrs: false,

  props: {
    loading: {
      type: Boolean,
      required: false,
      default: false
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    editable: {
      type: Boolean,
      required: false,
      default: false
    },
    modelId: {
      type: [Number, String],
      required: false,
      default: null
    },
    modelType: {
      type: String,
      required: false,
      default: null
    },
    enableNotesNotifications: {
      type: Boolean,
      required: false,
      default: false
    },
    hideActions: {
      type: Boolean,
      required: false,
      default: false
    },
    buttonSubmitText: {
      type: String,
      required: false,
      default: function () {
        return this.$t('fresh-vue-cards.misc.submit')
      }
    },
    mode: {
      type: String,
      required: false,
      default: 'VIEW',
      validator: (value) => ['VIEW', 'EDIT', 'NEW'].includes(value)
    },
    tab: {
      type: String,
      required: false,
      default: null
    },

    // see "TabsBase" component
    tabs: {
      type: Array,
      required: false,
      default: null
    }
  },

  data() {
    return {
      internalMode: 'VIEW',
      internalTab: null
    }
  },

  computed: {
    showActions() {
      return !this.hideActions && ['EDIT', 'NEW'].includes(this.internalMode)
    },

    showActionNotes() {
      return (
        'NotesDialog' in this.$options.components && // check optional component
        this.modelId &&
        this.modelType &&
        this.mode === 'VIEW'
      )
    },

    showActionEdit() {
      return this.editable && this.mode === 'VIEW'
    },

    showToolbarActions() {
      const hasSlots =
        this.$slots['toolbar-icon-actions-prepend'] ||
        this.$slots['toolbar-icon-actions-append']

      return (
        !this.loading &&
        (hasSlots || this.showActionNotes || this.showActionEdit)
      )
    },

    internalDisabled() {
      return this.disabled || this.loading
    }
  },

  watch: {
    modelId: {
      handler() {
        if (!this.loading) {
          this.updateMode()
        }
      },
      immediate: true
    },

    mode: {
      handler() {
        this.updateMode()
      },
      immediate: true
    },

    tab: {
      handler(value) {
        this.internalTab = value
      },
      immediate: true
    },

    loading() {
      this.updateMode()
    },

    internalMode: {
      handler: function (mode) {
        this.$emit('update:mode', mode)
      },
      immediate: true
    },

    internalTab: {
      handler(value) {
        this.$emit('update:tab', value)
      }
    }
  },

  created() {
    // import optional components

    try {
      this.$options.components['NotesDialog'] =
        require('@freshsolutions/fresh-vue-notes/src/components/NotesDialog.vue').default
    } catch (e) {
      // do nothing
    }
  },

  methods: {
    onEdit() {
      this.internalMode = 'EDIT'
    },

    onCancel() {
      this.internalMode = 'VIEW'
      this.$emit('cancel')
    },

    updateMode() {
      if (this.modelId) {
        if (['VIEW', 'EDIT'].includes(this.mode)) {
          this.internalMode = this.mode
        } else {
          this.internalMode = 'VIEW'
        }
      } else {
        this.internalMode = 'NEW'
      }
    }
  }
}
</script>
